/**
 * O relógio da organização (`O-03`, ADR-059).
 *
 * Numa organização de demonstração, o supervisor avança o relógio — 24 h, 7, 8, 15 e 30 dias
 * — para mostrar o prazo vencendo sem esperar. O tempo passa **só nela**: o servidor e os
 * outros clientes seguem no relógio de sempre. A API conta tudo nesse tempo (os prazos, o
 * "hoje", as janelas de 24 h); a tela precisa contar igual, ou o "vence em 3 min" do trânsito
 * aprovado agora diria "vencido" porque o relógio do navegador ficou um mês para trás.
 *
 * Por isso toda conta de prazo da tela passa por `agoraDoInquilino()`, e não por `new Date()`.
 * Fora de demonstração o deslocamento é zero e as duas são a mesma coisa. O que continua no
 * relógio do navegador é o que é da pessoa e não do trânsito: a inatividade da sessão, o "lido
 * às 10h32" de uma tela, o nome de um arquivo.
 */
import { useSyncExternalStore } from "react";

/** O evento que diz às telas que o tempo da organização andou: recarreguem o que mostram. */
export const EVENTO_DO_RELOGIO = "cockpit:relogio";

let deslocamentoMs = 0;
const ouvintes = new Set<() => void>();

/** Quanto o relógio da organização está à frente do navegador, em milissegundos. */
export function deslocamentoDoRelogio(): number {
  return deslocamentoMs;
}

/** O agora da organização: o do navegador mais o relógio da demonstração. */
export function agoraDoInquilino(): Date {
  return new Date(Date.now() + deslocamentoMs);
}

/**
 * Lido de `/demonstracao/relogio` — na entrada, de minuto em minuto, e depois de cada avanço.
 * Quando muda, as telas são avisadas (`EVENTO_DO_RELOGIO`): a janela das 24 h da Operação, o
 * período dos Indicadores e a fila recalculam — inclusive na primeira leitura depois de um F5,
 * quando a tela já tinha contado com o relógio do navegador.
 */
export function definirDeslocamento(segundos: number): void {
  // Uma resposta sem o número (ou com lixo) não pode envenenar o relógio de todas as telas:
  // `NaN` faria todo `agoraDoInquilino()` ser uma data inválida.
  const ms = Number.isFinite(segundos)
    ? Math.max(0, Math.round(segundos * 1000))
    : 0;
  if (ms === deslocamentoMs) return;
  deslocamentoMs = ms;
  for (const ouvir of ouvintes) ouvir();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(EVENTO_DO_RELOGIO));
  }
}

function assinar(ouvir: () => void): () => void {
  ouvintes.add(ouvir);
  return () => ouvintes.delete(ouvir);
}

/** O deslocamento, para a tela que muda quando ele muda (o selo do topo, os prazos). */
export function useDeslocamento(): number {
  return useSyncExternalStore(assinar, deslocamentoDoRelogio);
}

/** "+8 d", "+1 d 1 h" — o selo do topo. Vazio no relógio de sempre. */
export function rotuloDoDeslocamento(ms: number): string {
  if (ms <= 0) return "";
  if (ms < 3_600_000) return `+${Math.max(1, Math.round(ms / 60_000))} min`;
  const horas = Math.floor(ms / 3_600_000);
  const dias = Math.floor(horas / 24);
  const resto = horas % 24;
  return `+${[dias ? `${dias} d` : "", resto ? `${resto} h` : ""].filter(Boolean).join(" ")}`;
}
